import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { toLocalTime } from '@lifetools/shared-utils-time';
import { isIntegrationTestEnvironment } from 'utils/app/isIntegrationTestEnvironment';
import { applyDrag } from 'utils/schedules/layout/applyDrag';
import { layoutSchedule } from 'utils/schedules/layout/layoutSchedule';
import { captureSlotGeometry } from '../utils/captureSlotGeometry';
import { getBlockId } from '../utils/getBlockId';
import { resolveSlot } from '../utils/resolveSlot';

// Collects every scrollable ancestor of `node` (plus the document's scrolling element). Used to
// compensate the frozen collision rects for mid-drag scrolling — see `detectCollisions`.
function collectScrollParents(node) {
  const parents = [];
  let element = node?.parentElement;

  while (element) {
    const style = getComputedStyle(element);

    if (/(auto|scroll|overlay)/.test(style.overflowY + style.overflowX)) {
      parents.push(element);
    }

    element = element.parentElement;
  }

  parents.push(document.scrollingElement ?? document.documentElement);

  return parents;
}

const sumScrollOffsets = (parents) => parents.reduce(
  (offset, element) => ({
    x: offset.x + element.scrollLeft,
    y: offset.y + element.scrollTop,
  }),
  { x: 0, y: 0 },
);

// --- Interaction-rule guards (named so the FSM transitions read declaratively) ---

/**
 * Real pointers jitter by a pixel or two while "holding still"; a DIFFERENT target within this
 * dead-band of where the current one was accepted is ignored (else sitting on a row boundary
 * flips the target — and the whole preview — back and forth: visible flicker).
 */
const withinHysteresis = (pointer, anchor) =>
  !!anchor && Math.abs(pointer.y - anchor.y) < 4 && Math.abs(pointer.x - anchor.x) < 4;

/**
 * The Scheduler's drag-and-drop session: an explicit state machine over dnd-kit's events.
 *
 *   idle --(onDragStart)--> dragging --(onDragEnd: hand draft to onDrop)--> idle
 *                                \---(onDragCancel)---------------------->/
 *
 * While `dragging`, every semantic-target change recomputes the preview as a pure function of
 * the drag-start stored model: `layoutSchedule(applyDrag(originModel, target))`. The drop hands
 * that draft model to `onDrop` synchronously (the pending-model layer renders it from that
 * instant and persists in the background), so ending the drag cannot change the schedule's
 * shape. The session ref groups its state by concern:
 *
 *  - `models`   — origin blocks (pristine revert target), origin/draft stored models, intents.
 *  - `geometry` — the frozen droppable rects and the scroll bookkeeping that keeps the
 *                 pointer -> frozen-rect mapping honest across user scrolling and
 *                 layout-induced scroll shifts.
 *  - `targeting`— the last resolved target, pointer latch, and hysteresis anchor.
 *
 * Cleanup is keyed to the session's identity, so a drop whose async persistence is still in
 * flight can never clobber (or double-clean) a newer drag.
 */
export function useSchedulerDrag({
  layoutState,
  startTime,
  endTime,
  interval,
  compact,
  onDrop,
}) {
  const [draggingItem, setDraggingItem] = useState(null);
  const [draggingSchedule, setDraggingSchedule] = useState();
  const sessionRef = useRef(null);

  let blocks = draggingSchedule ?? layoutState.blocks;

  // Empty slots only ever render in the expanded view. The layout engine always tiles free
  // time with empty blocks (as does the drag preview), so filter them out here.
  if (compact) {
    blocks = blocks.filter(block => block.intent);
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  // Collide against the droppable rects captured when the drag started, not the live ones.
  // The drag preview re-lays-out blocks whose heights differ by duration, so live rects
  // shift under a stationary pointer: each re-layout changes the `over` target, which
  // triggers another reschedule and re-layout, looping until React throws "Maximum update
  // depth exceeded". Freezing the geometry makes `over` a pure function of pointer position.
  const detectCollisions = useCallback(args => {
    const session = sessionRef.current;

    if (!session) {
      return closestCenter(args);
    }

    const { models, geometry, targeting } = session;

    if (!geometry.bands) {
      // The drag-start slot geometry: pointer targeting is resolved against these bands
      // (drag-start rects joined with the origin block list) for the whole drag, never
      // against live droppables — the preview can re-tile, unmount, or re-order blocks
      // without ever perturbing targeting.
      geometry.bands = captureSlotGeometry(
        new Map(args.droppableRects),
        models.origin,
        args.active?.id,
      );

      // Capture the schedule's scrollable ancestors and their scroll positions alongside the
      // bands, so the frozen geometry can be scroll-compensated below.
      const anchor =
        args.droppableContainers?.find(container => container.id === args.active?.id)
          ?? args.droppableContainers?.[0];
      geometry.scrollParents = collectScrollParents(anchor?.node?.current);
      geometry.initialScroll = sumScrollOffsets(geometry.scrollParents);

      // Browser scroll anchoring reacts to the preview re-ordering rows by adjusting the scroll
      // position to keep its anchor node visually stationary. Each adjustment fires a scroll
      // event, the scroll re-resolves `over`, the new `over` re-orders the preview, and the
      // re-order triggers another anchoring adjustment — a self-sustaining scroll <-> re-layout
      // storm that pegs the main thread and hangs the tab whenever the schedule is scrolled
      // mid-drag. Disable anchoring on the schedule's scroll containers for the duration of the
      // drag (restored in `endSession`).
      geometry.anchorOverrides = geometry.scrollParents.map(element => {
        const previous = element.style.overflowAnchor;
        element.style.overflowAnchor = 'none';
        return { element, previous };
      });
    }

    // Recompute `over` ONLY when the pointer has actually moved. dnd-kit re-runs collision
    // detection on every scroll event too — but a scroll with a stationary pointer is either
    // (a) the user wheel-scrolling mid-drag, in which case their very next pointer movement
    // retargets correctly via the scroll compensation below, or (b) a LAYOUT-induced scrollTop
    // shift, which must never retarget on its own. Latching `over` to pointer movement breaks
    // every such feedback loop by construction: layout and scroll events alone cannot retarget.
    const pointer = args.pointerCoordinates;

    if (!pointer) {
      return targeting.lastCollisions ?? [];
    }

    if (
      targeting.lastCollisions &&
      targeting.lastPointer &&
      pointer.x === targeting.lastPointer.x && pointer.y === targeting.lastPointer.y
    ) {
      return targeting.lastCollisions;
    }

    // The frozen rects are VIEWPORT positions captured at drag start; scrolling moves the live
    // content relative to them. Translate the pointer INTO the frozen coordinate space by the
    // accumulated USER scroll delta, so after a mid-drag scroll the pointer still resolves the
    // block that is visually under it. `scrollPollution` subtracts the LAYOUT-induced scrollTop
    // shifts (a preview changes the content height, so the scroll container clamps/adjusts —
    // measured around each preview render below); without that correction the mapping drifts by
    // roughly a row and the drag targets the wrong block.
    const scroll = sumScrollOffsets(geometry.scrollParents);
    const pollution = geometry.scrollPollution ?? { x: 0, y: 0 };
    const frozenY = pointer.y + (scroll.y - geometry.initialScroll.y - pollution.y);

    // Resolve the SLOT BAND under the pointer with a pure vertical hit-test over the frozen
    // geometry — never dnd-kit's built-in strategies (`pointerWithin` only consults MOUNTED
    // droppables, and `closestCenter` measures from the dragged block's centre, rows away from
    // the pointer for a tall block). X is ignored deliberately: the drag is restricted to the
    // vertical axis and every row spans the table.
    const band = resolveSlot(geometry.bands, frozenY);

    if (!band) {
      return targeting.lastCollisions ?? [];
    }

    // Hysteresis is applied on OWNER change only; movement within a band never flickers.
    if (band.ownerId !== targeting.currentOwnerId) {
      if (withinHysteresis(pointer, targeting.pointerAtOverChange)) {
        targeting.lastPointer = { ...pointer };
        return targeting.lastCollisions ?? [];
      }

      targeting.currentOwnerId = band.ownerId;
      targeting.pointerAtOverChange = { ...pointer };

      // The DECLARATIVE targeting rules, written on the band's owner type:
      //  - the active block's own original band => revert the preview to the pristine
      //    drag-start layout (dropping there is a no-op);
      //  - an appointment band => HOLD the current preview (an appointment is not a drop
      //    target; the dragged block stays where the last real target put it);
      //  - anything else => that band is the semantic drop target.
      // The semantic target lives on the session and is consumed by `doDragMove` — dnd-kit's
      // `over` is bookkeeping only, so a target whose droppable the preview unmounted (re-tiled
      // empties) still targets and previews correctly.
      if (band.ownerType === 'active') {
        targeting.semanticTarget = { kind: 'origin' };
      } else if (band.ownerType !== 'appointment') {
        targeting.semanticTarget = { kind: 'slot', targetOffset: band.offset };
      }
    }

    // dnd-kit bookkeeping: report a MOUNTED droppable (the band owner's when still mounted,
    // else the active block's own — always mounted as the drag placeholder).
    const container =
      args.droppableContainers?.find(droppable => droppable.id === band.ownerId) ??
      args.droppableContainers?.find(droppable => droppable.id === args.active?.id);
    const result = container
      ? [{ id: container.id, data: { droppableContainer: container, value: 0 } }]
      : [];

    targeting.lastPointer = { ...pointer };
    targeting.lastCollisions = result;

    return result;
  }, []);

  const doDragStart = useCallback((event) => {
    const activeBlock = blocks.find(block => getBlockId(block) === event.active.id);

    // A stale pointer-down can race a snapshot-driven re-render inside the sensor's activation
    // window, leaving no block for the activated id; ignore the drag rather than crash.
    if (!activeBlock) {
      return;
    }

    const blocksCopy = [...blocks];

    // `origin`/`originModel` snapshot the schedule as it was when the drag started; every
    // `doDragOver` recomputes from them so repeated drag-over events never accumulate
    // displacement. `draftModel` is the stored model the current preview renders — the exact
    // value a drop persists.
    sessionRef.current = {
      phase: 'dragging',
      models: {
        origin: blocksCopy,
        originModel: layoutState.effectiveModel,
        intentsByTodoId: layoutState.intentsByTodoId,
        draftModel: layoutState.effectiveModel,
      },
      geometry: {
        bands: null,
        scrollParents: null,
        initialScroll: null,
        anchorOverrides: null,
        scrollPollution: { x: 0, y: 0 },
        scrollBeforePreview: null,
      },
      targeting: {
        currentOwnerId: null,
        semanticTarget: null,
        appliedTarget: null,
        lastPointer: null,
        lastCollisions: null,
        pointerAtOverChange: null,
      },
    };

    setDraggingSchedule(blocksCopy);
    setDraggingItem(activeBlock);
  }, [ blocks, layoutState ]);

  // Consumes the semantic target `detectCollisions` resolved. dnd-kit's `over` no longer
  // drives the preview at all: a target keeps previewing correctly even when its droppable
  // was unmounted by an earlier preview (re-tiled expanded-mode empties).
  const doDragMove = useCallback(event => {
    const { active } = event;
    const session = sessionRef.current;

    // Bail out if there's no active drag (stale event firing after cleanup)
    if (!session) {
      return;
    }

    const { models, targeting, geometry } = session;
    const target = targeting.semanticTarget;

    // The preview is a pure function of the drag-start model and the semantic target, so an
    // unchanged target cannot change the result; skip the recompute and re-render.
    if (!target || target === targeting.appliedTarget) {
      return;
    }

    targeting.appliedTarget = target;

    // Back over the active block's own ORIGINAL slot: revert the preview to the pristine
    // drag-start layout. Dropping here is then a no-op that leaves the block at its original
    // time. (Without this, the last preview stuck and the original slot was the one place the
    // block could never be dropped back into.)
    if (target.kind === 'origin') {
      models.draftModel = models.originModel;

      setDraggingSchedule(models.origin);
      setDraggingItem(models.origin.find(block => getBlockId(block) === active.id));
      return;
    }

    // Always recompute from the drag-start MODEL, never from the previous preview. Chaining
    // drags on their own output accumulates displacement: dragging down several blocks and
    // back up leaves every passed block pushed later, opens gaps and can push blocks past the
    // schedule end where they no longer render.
    const activeBlock = models.origin.find(block => getBlockId(block) === active.id);

    if (!activeBlock) {
      console.warn('doDragMove: missing active block', JSON.stringify({ activeId: active.id }));
      return;
    }

    // The semantic drag: a new stored model from `applyDrag`, rendered by the SAME layout
    // engine as the normal schedule. The preview and the persisted drop result are the same
    // value by construction.
    const config = { startTime, endTime, interval };
    const draft = applyDrag(
      models.originModel,
      {
        activeTodoId: `${activeBlock.intent?.todo?.id}`,
        targetOffset: target.targetOffset,
      },
      { intentsByTodoId: models.intentsByTodoId, config },
    );
    const newBlocks = layoutSchedule(draft, models.intentsByTodoId, config);
    const newActiveBlock = newBlocks.find(block => getBlockId(block) === active.id);

    models.draftModel = draft;

    // Snapshot the scroll positions BEFORE the preview renders; the layout effect below
    // measures them again after layout and books any difference as layout-induced pollution
    // (see the delta computation in `detectCollisions`).
    if (geometry.scrollParents) {
      geometry.scrollBeforePreview = sumScrollOffsets(geometry.scrollParents);
    }

    setDraggingSchedule(newBlocks);
    setDraggingItem(newActiveBlock);
  }, [ startTime, endTime, interval ]);

  // Measures, right after a preview render commits layout, how far the scroll containers moved
  // as a SIDE EFFECT of the re-layout (content height changes make the browser clamp/adjust
  // scrollTop). That shift is not user scrolling and must not shift the pointer -> frozen-rect
  // mapping, so it accumulates into `scrollPollution`, which `detectCollisions` subtracts.
  useLayoutEffect(() => {
    const geometry = sessionRef.current?.geometry;

    if (!geometry?.scrollBeforePreview || !geometry.scrollParents) {
      return;
    }

    const after = sumScrollOffsets(geometry.scrollParents);

    geometry.scrollPollution = {
      x: geometry.scrollPollution.x + (after.x - geometry.scrollBeforePreview.x),
      y: geometry.scrollPollution.y + (after.y - geometry.scrollBeforePreview.y),
    };
    geometry.scrollBeforePreview = null;
  }, [ draggingSchedule ]);

  // Clear the drag state, but only if `session` is still the active session — a newer drag
  // may have started while this session's drop was being persisted.
  const endSession = useCallback((session) => {
    if (session && sessionRef.current === session) {
      // Restore the scroll-anchoring styles disabled for the drag (see `detectCollisions`).
      for (const { element, previous } of session.geometry.anchorOverrides ?? []) {
        element.style.overflowAnchor = previous;
      }

      sessionRef.current = null;
      setDraggingSchedule(null);
      setDraggingItem(null);
    }
  }, []);

  const doDragEnd = useCallback((event) => {
    const session = sessionRef.current;

    if (!session) {
      return;
    }

    session.phase = 'dropping';

    const { originModel, draftModel, intentsByTodoId } = session.models;

    // The drop hands the draft model the preview rendered to the pending-model layer —
    // the same value, so the handoff cannot change the schedule's shape: the preview
    // disappears and the pending render takes over byte-identically, while persistence
    // settles in the background. The changed intents are the stored-model diff: every
    // todo whose start differs from the drag-start model.
    const changedIntents = draftModel === originModel ? [] : draftModel
      .filter(entry => {
        const before = originModel.find(previous => previous.todo === entry.todo);
        return before && before.start !== entry.start && intentsByTodoId[entry.todo];
      })
      .map(entry => ({
        ...intentsByTodoId[entry.todo],
        start: toLocalTime(entry.start),
      }));

    if (changedIntents.length > 0) {
      onDrop(draftModel, originModel, changedIntents);
    }

    endSession(session);
  }, [ onDrop, endSession ]);

  const doDragCancel = useCallback(() => {
    endSession(sessionRef.current);
  }, [ endSession ]);

  return {
    blocks,
    draggingItem,
    dndProps: {
      sensors,
      collisionDetection: detectCollisions,
      modifiers: [restrictToVerticalAxis],
      // Disable dnd-kit's auto-scroll only under Cypress: a programmatic drag near a scroll
      // edge would otherwise scroll the schedule out from under the synthetic pointer and land
      // the drop on the wrong slot. Real users keep auto-scroll. [test hook]
      autoScroll: !isIntegrationTestEnvironment(),
      onDragStart: doDragStart,
      onDragMove: doDragMove,
      onDragEnd: doDragEnd,
      onDragCancel: doDragCancel,
    },
  };
}
